import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

const TOKEN_KEY    = 'pb_auth_token'
const EXPIRY_KEY   = 'pb_auth_expiry'
const TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000  // 7 days

interface AuthContextValue {
  token: string | null
  isAuthenticated: boolean
  login: (token: string) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function readStoredToken(): string | null {
  try {
    const token  = localStorage.getItem(TOKEN_KEY)
    const expiry = localStorage.getItem(EXPIRY_KEY)
    if (!token || !expiry) return null
    if (Date.now() > parseInt(expiry, 10)) {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(EXPIRY_KEY)
      return null
    }
    return token
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(readStoredToken)

  const login = useCallback((newToken: string) => {
    const expiry = Date.now() + TOKEN_TTL_MS
    localStorage.setItem(TOKEN_KEY,  newToken)
    localStorage.setItem(EXPIRY_KEY, String(expiry))
    setToken(newToken)
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(EXPIRY_KEY)
    setToken(null)
  }, [])

  return (
    <AuthContext.Provider value={{ token, isAuthenticated: !!token, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
